"use client";

import * as React from "react";

const NOTES = ["♪", "♫", "♬", "♩", "♪", "♫", "♬"];
/** Mostly the brand orange, with a few warm neighbours so the burst reads as confetti. */
const COLORS = [
  "var(--primary)",
  "#ffb347",
  "var(--primary)",
  "#ff6f91",
  "var(--primary)",
  "#ffd166",
  "#ff8c42",
];

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** `[shot, fire]` — `fire()` plays the burst again (a no-op for listeners who asked for reduced motion). */
export function useLikeBurst(): [number, () => void] {
  const [shot, setShot] = React.useState(0);
  const fire = React.useCallback(() => {
    if (!reducedMotion()) setShot((n) => n + 1);
  }, []);
  return [shot, fire];
}

/**
 * Twitter's heart burst, with music notes: the icon pops, a primary-coloured
 * ring pulses out from it and a handful of notes fly off radially and fade.
 * Web Animations API (no global keyframes); `shot` changing replays it.
 */
export function LikeBurst({
  shot,
  size = 20,
  children,
}: {
  shot: number;
  /** The icon's size in px — the ring and the notes' flight scale with it. */
  size?: number;
  children: React.ReactNode;
}) {
  const iconRef = React.useRef<HTMLSpanElement>(null);
  const layerRef = React.useRef<HTMLSpanElement>(null);

  React.useLayoutEffect(() => {
    const icon = iconRef.current;
    const layer = layerRef.current;
    if (!shot || !icon || !layer || typeof icon.animate !== "function") return;
    const animations: Animation[] = [];

    animations.push(
      icon.animate(
        [
          { transform: "scale(1)" },
          { transform: "scale(0.55)", offset: 0.15 },
          { transform: "scale(1.3)", offset: 0.55 },
          { transform: "scale(0.95)", offset: 0.8 },
          { transform: "scale(1)" },
        ],
        { duration: 500, easing: "ease-out" },
      ),
    );

    const ring = layer.querySelector<HTMLElement>("[data-ring]");
    if (ring) {
      animations.push(
        ring.animate(
          [
            { transform: "translate(-50%, -50%) scale(0.2)", opacity: 0.9, borderWidth: "6px" },
            { transform: "translate(-50%, -50%) scale(1.9)", opacity: 0, borderWidth: "0px" },
          ],
          { duration: 550, easing: "cubic-bezier(.2,.7,.3,1)", fill: "forwards" },
        ),
      );
    }

    const notes = layer.querySelectorAll<HTMLElement>("[data-note]");
    const distance = size * 1.4;
    notes.forEach((note, i) => {
      // Evenly round the circle, nudged so it never looks like a clock face.
      const angle = (i / notes.length) * Math.PI * 2 + ((i * 47) % 30) / 100 - 0.15;
      const reach = distance * (0.85 + ((i * 37) % 35) / 100);
      const x = Math.cos(angle) * reach;
      const y = Math.sin(angle) * reach;
      const spin = ((i % 2 ? 1 : -1) * (20 + ((i * 23) % 25))).toFixed(0);
      animations.push(
        note.animate(
          [
            { transform: "translate(-50%, -50%) scale(0.3)", opacity: 0 },
            { opacity: 1, offset: 0.2 },
            {
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(1) rotate(${spin}deg)`,
              opacity: 0.9,
              offset: 0.7,
            },
            {
              transform: `translate(calc(-50% + ${x * 1.15}px), calc(-50% + ${y * 1.15 - 4}px)) scale(0.8) rotate(${spin}deg)`,
              opacity: 0,
            },
          ],
          {
            duration: 750 + (i % 3) * 60,
            delay: 70,
            easing: "cubic-bezier(.15,.8,.3,1)",
            fill: "both",
          },
        ),
      );
    });

    return () => animations.forEach((a) => a.cancel());
  }, [shot, size]);

  return (
    <span className="relative inline-flex">
      <span ref={iconRef} className="inline-flex">
        {children}
      </span>
      {shot > 0 && (
        <span
          key={shot}
          ref={layerRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-visible"
        >
          <span
            data-ring
            className="border-primary absolute top-1/2 left-1/2 rounded-full border-solid opacity-0"
            style={{ width: size * 1.5, height: size * 1.5 }}
          />
          {NOTES.map((note, i) => (
            <span
              key={i}
              data-note
              className="absolute top-1/2 left-1/2 leading-none font-bold opacity-0"
              style={{ color: COLORS[i], fontSize: Math.max(10, Math.round(size * 0.65)) }}
            >
              {note}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
